<template>
    <div class="row dashboard-listing">
        <template v-if="!isInitialLoaded">
            <transition>
                <div class="col-lg-12" v-loading="true" :element-loading-text="'Loading '+peer.common.programName('p')+'...'" style="height:350px;"></div>
            </transition>
        </template>
        <template v-else>
            <template v-if="projects.length">
                <div class="col-lg-8">
                    <div class="projects-list">
                        <div v-for="(project, index) in projects" :key="index" class="project-box">
                            <div class="project-head">
                                <div class="project-title">
                                    <router-link :to="peer.project.isRegistrationPaymentPending(project) ?'/'+program+'/payment/'+project.id+'?for=registration' :'/'+program+'/details/'+project.id">
                                        <h5>{{ project.name }}</h5>
                                        <div class="project-id">{{ project.id }}</div>
                                    </router-link>
                                </div>
                                <div class="project-status">{{ project.status }}</div>
                            </div>
                            <div class="project-info">
                                <div class="row align-items-center">
                                    <div class="col-6">
                                        <div class="row">
                                            <div class="col-5">
                                                <div class="el-tablo highlight">
                                                    <div class="label">Peak Megawatts (MW)</div>
                                                    <div v-if="project.peerRs && project.peerRs.peakMw" class="value">{{ project.peerRs.peakMw }}</div>
                                                    <div v-else>--</div>
                                                </div>
                                            </div>
                                            <div class="col-7">
                                                <div class="el-tablo highlight">
                                                    <div class="label">Annual Megawatt Hours (MWh)</div>
                                                    <div v-if="project.peerRs && project.peerRs.annualMwHrs" class="value">{{ project.peerRs.annualMwHrs }}</div>
                                                    <div v-else>--</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-6 project-location">
                                        <font-awesome-icon icon="map-marker-alt"/> {{ peer.lists.address(project) }}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <infinite-loading @infinite="infiniteHandler" spinner="waveDots">
                            <div slot="no-more" style="margin-top:25px;color:#9b9898;">--- All {{ peer.common.programName('p') }} loaded ---</div>
                            <div slot="no-results" style="margin-top:25px;color:#9b9898;">--- All {{ peer.common.programName('p') }} loaded ---</div>
                        </infinite-loading>
                    </div>
                </div>
                <div class="col-lg-4">
                    <div v-sticky="{ stickyTop: 5, zIndex: 1 }" class="element-wrapper">
                        <div class="element-box activity-logs">
                            <h6 class="element-header">
                                <font-awesome-icon icon="road"/> User Activity
                            </h6>
                            <perfect-scrollbar ref="ps" class="timed-activities compact" :options="{
                                wheelPropagation: true,
                                minScrollbarLength: 100
                            }">
                                <activity-logs
                                    :isList="true"
                                    scroll-node=".timed-activities"
                                    :state.sync="activityLogsState"
                                    :path="peer.lists.programCode(program).code"/>
                            </perfect-scrollbar>
                        </div>
                    </div>
                </div>
            </template>
            <template v-else-if="isNoProjects">
                <div class="col-lg-12">
                    <div class="element-box">
                        <div class="element-box-content">
                            <div class="alert" role="alert">
                                <h4 class="alert-heading">
                                    Welcome to PEER Online!
                                </h4>
                                <p>
                                    Currently there are no {{ peer.lists.programCode(program).program }} registered. You can get started by clicking Create New {{ peer.common.programName() }} button.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </template>
    </div>
</template>

<script>
import ActivityLogs from '@/components/ActivityLogs.vue'

export default {
    components: {
        ActivityLogs
    },
    
    data() {
        return {
            isNoProjects: false,
            isAllLoaded: false,
            isInitialLoaded: false,
            activityLogsState: 0,
            projects: []
        }
    },
    
    computed: {
        program() {
            return this.peer.common.programName('r')
        }
    },
    
    watch: {
        '$route'() {
            this.boot(true)
        }
    },
    
    mounted() {
        this.boot()
    },
    
    methods: {
        boot(force) {
            this.isNoProjects = false
            this.isAllLoaded = false
            this.isInitialLoaded = false
            this.activityLogsState = 0
            this.projects = []
            
            this.initialLoad().always(() => {
                if(!this.projects.length)
                    this.isNoProjects = true
                
                this.isInitialLoaded = true
                
                if(force)
                    this.$forceUpdate()
                
                this.activityLogSticky()
            })
        },
        
        initialLoad() {
            let wait = this.peer.$.Deferred()
            
            let cache = this.peer.lists.getLists(true, this.program)
            
            if(cache.loaded === 2) {
                this.projects = this.peer.misc.arr(cache.data, 0, cache.total)
                this.isAllLoaded = !(cache.npk && cache.npk !== '')
                return wait.resolve().promise()
            }
            
            this.loadProjects().then(
                () => {
                    return wait.resolve().promise()
                },
                () => {
                    return wait.reject().promise()
                }
            )
            
            return wait.promise()
        },
        
        loadProjects() {
            let wait = this.peer.$.Deferred()
            this.peer.misc.blockScreenTransparent()
            this.peer.lists.getLists({
                f: {
                    id: ''
                }
            }, this.program).done((res) => {
                this.projects = this.projects.concat(this.peer.misc.arr(res.data))
                this.isAllLoaded = !(res.npk && res.npk !== '')
                
                return wait.resolve().promise()
            }).fail(() => {
                return wait.reject().promise()
            }).always(() => {
                this.peer.misc.releaseScreen()
            })
            
            return wait.promise()
        },
        
        activityLogSticky() {
            this.$nextTick(function() {
                this.peer.$('.activity-logs .timed-activities').css({
                    'max-height': (this.peer.$(window).height() - 127)+'px',
                    'overflow-y': 'auto'
                })
                
                if(this.$refs.ps)
                    this.$refs.ps.update()
            })
        },
        
        infiniteHandler($state) {
            if(this.isAllLoaded)
                $state.complete()
            else {
                this.loadProjects().always(() => {
                    if(this.isAllLoaded)
                        $state.complete()
                    else
                        $state.loaded()
                    
                    this.activityLogSticky()
                })
            }
        }
    }
}
</script>

<style scoped>
.activity-logs {
    padding-right: 3px;
}
.timed-activities {
    position: relative;
    padding-right: 20px;
    margin-bottom: 0;
}
</style>
